import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import "../styles/Purchases.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faShoppingCart,
    faCalendarAlt,
    faBox,
    faPlus,
    faMinus,
    faTrash,
    faSave,
    faTimes,
    faSpinner,
    faCheckCircle,
    faExclamationCircle,
    faSearch,
    faUpload,
    faReceipt,
    faChevronDown,
    faChevronUp,
    faBoxes,
    faBarcode,
    faTag,
    faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';

// Create translator instance with improved caching and sync
const createPurchasesTranslator = () => {
    let cache = new Map();
    let preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);
    let translationListeners = new Set();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ourzest_purchases_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in Purchases Form");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ourzest_purchases_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed, trying MyMemory");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        addPurchase: 'Add Stock',
        editPurchase: 'Edit Stock',
        purchaseDetails: 'Stock Details',
        date: 'Date',
        billFile: 'Bill File (Optional)',
        chooseFile: 'Choose file',
        noFile: 'No file chosen',
        items: 'Items',
        addItem: 'Add Item',
        productName: 'Product Name',
        batchNumber: 'Batch Number',
        unit: 'Unit',
        quantity: 'Quantity',
        remove: 'Remove',
        savePurchase: 'Save Stock',
        updatePurchase: 'Update Stock',
        clear: 'Clear',
        saving: 'Saving...',
        purchaseSaved: 'Stock saved successfully!',
        purchaseUpdated: 'Stock updated successfully!',
        failedSave: 'Failed to save stock',
        failedUpdate: 'Failed to update stock',
        networkError: 'Network error occurred',
        dateRequired: 'Date is required',
        itemsRequired: 'At least one item is required',
        productRequired: 'Product name is required',
        batchRequired: 'Batch number is required',
        unitRequired: 'Unit is required',
        quantityRequired: 'Quantity is required',
        invalidQuantity: 'Please enter a valid quantity',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        piece: 'Piece',
        box: 'Box',
        packet: 'Packet',
        kg: 'Kilogram',
        gram: 'Gram',
        liter: 'Liter',
        ml: 'Milliliter',
        dozen: 'Dozen',
        dabba: 'ڈبہ ',
        borai: 'بوری',
        selectUnit: 'Select unit',
        browse: 'Browse',
        optional: 'Optional',
        searchProduct: 'Search product...',
        noProductsFound: 'No products found',
        category: 'Category',
        company: 'Company',
        stockCategory: 'Stock Category'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    const addListener = (callback) => {
        translationListeners.add(callback);
    };

    const removeListener = (callback) => {
        translationListeners.delete(callback);
    };

    const notifyListeners = (lang) => {
        translationListeners.forEach(callback => callback(lang));
    };

    const checkLanguageChange = () => {
        const currentLang = localStorage.getItem('ourzest_language') || 'en';
        notifyListeners(currentLang);
    };

    const startLanguageCheck = () => {
        checkLanguageChange();
        const intervalId = setInterval(checkLanguageChange, 500);
        window.addEventListener('storage', checkLanguageChange);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', checkLanguageChange);
        };
    };

    loadFromCache();

    return {
        getTranslations,
        translateText,
        addListener,
        removeListener,
        startLanguageCheck
    };
};

const purchasesTranslator = createPurchasesTranslator();

// Toast Notification Component
const ToastNotification = ({ toast, setToast, translations }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success':
                return faCheckCircle;
            case 'error':
                return faExclamationCircle;
            case 'warning':
                return faExclamationTriangle;
            default:
                return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success':
                return translations?.success || 'Success';
            case 'error':
                return translations?.error || 'Error';
            case 'warning':
                return translations?.warning || 'Warning';
            default:
                return '';
        }
    };

    if (!toast.show) return null;

    const opacity = Math.max(toast.progress / 100, 0.1);

    return (
        <div
            className={`toast-notification ${toast.type} ${toast.direction === 'rtl' ? 'toast-rtl' : 'toast-ltr'}`}
            style={{ opacity }}
        >
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="toast-text">
                    <div className="toast-title">
                        {getTitleForType(toast.type)}
                    </div>
                    <div className="toast-message">
                        {toast.message}
                    </div>
                </div>
                <button
                    className="toast-close"
                    onClick={() => setToast(prev => ({ ...prev, show: false }))}
                >
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="toast-timer">
                <div
                    className="toast-progress-bar"
                    style={{
                        width: `${toast.progress}%`,
                        transition: 'width 0.1s linear'
                    }}
                ></div>
                <div className="toast-countdown">
                    {toast.timeLeft}s
                </div>
            </div>
        </div>
    );
};

// Items Table Component with product selection - ONLY SALEABLE PRODUCTS
const ItemsTable = ({
    items,
    onItemChange,
    onRemove,
    onAdd,
    errors,
    disabled,
    translations,
    isRTL,
    user,
    shopId,
    showToast
}) => {
    const tableRef = useRef(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [products, setProducts] = useState([]);
    const [filteredProductsByIndex, setFilteredProductsByIndex] = useState({});
    const [productSearchTerms, setProductSearchTerms] = useState({});
    const [showProductDropdown, setShowProductDropdown] = useState({});
    const [loadingProducts, setLoadingProducts] = useState(false);
    const productSearchRefs = useRef({});

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const loadProducts = async () => {
        if (!user?.username || !shopId) return;

        setLoadingProducts(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/products.php', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    action: 'get_all_products',
                    username: user?.username,
                    shop_id: shopId
                })
            });

            const data = await response.json();

            if (data.status === 'success') {
                // Filter to show ONLY Saleable products (not Raw Materials)
                const saleableProducts = data.data.filter(product => 
                    product.stock_category === 'Saleable' || 
                    product.stock_category === 'saleable' ||
                    product.stock_category?.toLowerCase() === 'saleable' ||
                    !product.stock_category || // If no category, treat as saleable
                    (product.stock_category !== 'Raw Materials' && product.stock_category !== 'raw' && product.stock_category?.toLowerCase() !== 'raw')
                );
                setProducts(saleableProducts);
                console.log('Saleable products loaded (filtered):', saleableProducts.length);
            }
        } catch (error) {
            console.error('Error loading products:', error);
        } finally {
            setLoadingProducts(false);
        }
    };

    useEffect(() => {
        loadProducts();
    }, [user?.username, shopId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            Object.keys(productSearchRefs.current).forEach(index => {
                if (productSearchRefs.current[index] && !productSearchRefs.current[index].contains(event.target)) {
                    setShowProductDropdown(prev => ({ ...prev, [index]: false }));
                }
            });
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleProductSearch = (index, term) => {
        console.log(`Searching for index ${index}: "${term}"`);
        
        setProductSearchTerms(prev => ({ ...prev, [index]: term }));

        if (term.trim() === '') {
            setFilteredProductsByIndex(prev => ({ ...prev, [index]: [] }));
            setShowProductDropdown(prev => ({ ...prev, [index]: false }));
        } else {
            const filtered = products.filter(product =>
                product.product_name?.toLowerCase().includes(term.toLowerCase()) ||
                product.batch_no?.toLowerCase().includes(term.toLowerCase()) ||
                product.barcode?.toLowerCase().includes(term.toLowerCase())
            );
            console.log(`Found ${filtered.length} products for search "${term}"`);
            setFilteredProductsByIndex(prev => ({ ...prev, [index]: filtered }));
            setShowProductDropdown(prev => ({ ...prev, [index]: true }));
        }
    };

    // Auto-fill category, stock_category, company on product selection
    const selectProduct = (index, product) => {
        console.log('=== SELECTING PRODUCT ===', product);
               
        onItemChange(index, 'product_name', product.product_name || '');
        onItemChange(index, 'batch_no', product.batch_no || '');
        onItemChange(index, 'category', product.category || '');
        onItemChange(index, 'stock_category', product.stock_category || '');
        onItemChange(index, 'company', product.company || '');
        onItemChange(index, 'unit', product.unit || '');
        
        setProductSearchTerms(prev => ({ ...prev, [index]: product.product_name || '' }));
        setFilteredProductsByIndex(prev => ({ ...prev, [index]: [] }));
        setShowProductDropdown(prev => ({ ...prev, [index]: false }));
        
        if (showToast) {
            showToast('success', `Product "${product.product_name}" selected`);
        }
    };

    const units = [
        { value: 'piece', label: translations?.piece || 'Piece' },
        { value: 'box', label: translations?.box || 'Box' },
        { value: 'packet', label: translations?.packet || 'Packet' },
        { value: 'kg', label: translations?.kg || 'Kilogram' },
        { value: 'gram', label: translations?.gram || 'Gram' },
        { value: 'liter', label: translations?.liter || 'Liter' },
        { value: 'ml', label: translations?.ml || 'Milliliter' },
        { value: 'dozen', label: translations?.dozen || 'Dozen' },
        { value: 'dabba', label: 'dabba' },
        { value: 'burai', label: 'burai' },
    ];

    const getFilteredProductsForIndex = (index) => {
        return filteredProductsByIndex[index] || [];
    };

    // Mobile view
    if (isMobile) {
        return (
            <div className="items-mobile-container">
                <div className="items-header-mobile">
                    <h4>
                        <FontAwesomeIcon icon={faBoxes} />
                        {translations?.items || 'Items'}
                    </h4>
                    <button
                        type="button"
                        className="btn-add-item-mobile"
                        onClick={onAdd}
                        disabled={disabled}
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        <span>{translations?.addItem || 'Add Item'}</span>
                    </button>
                </div>
                <div className="items-cards-container">
                    {items.map((item, index) => {
                        const filteredProductsForIndex = getFilteredProductsForIndex(index);
                        const showDropdown = showProductDropdown[index] || false;
                        const searchTerm = productSearchTerms[index] !== undefined ? productSearchTerms[index] : item.product_name;
                        
                        return (
                            <div key={index} className="item-card-mobile">
                                <div className="item-card-header">
                                    <span className="item-number">
                                        <FontAwesomeIcon icon={faBox} className="header-icon-only" />
                                        Item {index + 1}
                                    </span>
                                    <button
                                        type="button"
                                        className="item-remove-btn-mobile"
                                        onClick={() => onRemove(index)}
                                        disabled={disabled || items.length === 1}
                                    >
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>

                                <div className="item-card-content">
                                    {/* Product Name with Search */}
                                    <div
                                        className={`mobile-field ${errors[`product_name_${index}`] ? 'has-error' : ''}`}
                                        ref={el => productSearchRefs.current[index] = el}
                                    >
                                        <label>
                                            <FontAwesomeIcon icon={faSearch} className="field-icon" />
                                            {translations?.productName || 'Product Name'}
                                        </label>
                                        <div className="product-search-container">
                                            <input
                                                type="text"
                                                value={searchTerm}
                                                onChange={(e) => handleProductSearch(index, e.target.value)}
                                                onFocus={() => {
                                                    if (searchTerm && searchTerm.trim()) {
                                                        handleProductSearch(index, searchTerm);
                                                    } else {
                                                        setShowProductDropdown(prev => ({ ...prev, [index]: true }));
                                                    }
                                                }}
                                                placeholder={translations?.searchProduct || 'Search product...'}
                                                disabled={disabled}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                                autoComplete="off"
                                            />
                                            {loadingProducts && (
                                                <div className="search-loading">
                                                    <FontAwesomeIcon icon={faSpinner} spin />
                                                </div>
                                            )}

                                            {showDropdown && filteredProductsForIndex.length > 0 && (
                                                <div className="product-dropdown">
                                                    {filteredProductsForIndex.map(product => (
                                                        <div
                                                            key={product.id}
                                                            className="product-option"
                                                            onClick={() => selectProduct(index, product)}
                                                        >
                                                            <div className="product-name">{product.product_name}</div>
                                                            <div className="product-details">
                                                                {product.batch_no && <span><FontAwesomeIcon icon={faTag} /> {product.batch_no}</span>}
                                                                {product.barcode && <span><FontAwesomeIcon icon={faBarcode} /> {product.barcode}</span>}
                                                            </div>
                                                            <div className="product-stock">Stock: {product.quantity} {product.unit}</div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {showDropdown && filteredProductsForIndex.length === 0 && searchTerm && (
                                                <div className="product-dropdown empty">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    {translations?.noProductsFound || 'No products found'}
                                                </div>
                                            )}
                                        </div>
                                        {errors[`product_name_${index}`] && (
                                            <div className="error-text">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {errors[`product_name_${index}`]}
                                            </div>
                                        )}
                                    </div>

                                    {/* Batch Number */}
                                    <div className={`mobile-field ${errors[`batch_no_${index}`] ? 'has-error' : ''}`}>
                                        <label>{translations?.batchNumber || 'Batch Number'}</label>
                                        <input
                                            type="text"
                                            value={item.batch_no || ''}
                                            onChange={(e) => onItemChange(index, 'batch_no', e.target.value)}
                                            placeholder={translations?.batchNumber || 'Batch number'}
                                            disabled={disabled}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                            style={{ backgroundColor: item.batch_no ? '#e8f5e9' : '', borderLeft: item.batch_no ? '3px solid #4caf50' : '' }}
                                        />
                                        {errors[`batch_no_${index}`] && (
                                            <div className="error-text">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {errors[`batch_no_${index}`]}
                                            </div>
                                        )}
                                    </div>

                                    {/* Category field */}
                                    <div className={`mobile-field ${errors[`category_${index}`] ? 'has-error' : ''}`}>
                                        <label>{translations?.category || 'Category'}</label>
                                        <input
                                            type="text"
                                            value={item.category || ''}
                                            onChange={(e) => onItemChange(index, 'category', e.target.value)}
                                            placeholder={translations?.category || 'Category'}
                                            disabled={disabled}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        {errors[`category_${index}`] && (
                                            <div className="error-text">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {errors[`category_${index}`]}
                                            </div>
                                        )}
                                    </div>

                                    {/* Stock Category field */}
                                    <div className={`mobile-field ${errors[`stock_category_${index}`] ? 'has-error' : ''}`}>
                                        <label>{translations?.stockCategory || 'Stock Category'}</label>
                                        <input
                                            type="text"
                                            value={item.stock_category || ''}
                                            onChange={(e) => onItemChange(index, 'stock_category', e.target.value)}
                                            placeholder={translations?.stockCategory || 'Stock Category'}
                                            disabled={disabled}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        {errors[`stock_category_${index}`] && (
                                            <div className="error-text">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {errors[`stock_category_${index}`]}
                                            </div>
                                        )}
                                    </div>

                                    {/* Company field */}
                                    <div className={`mobile-field ${errors[`company_${index}`] ? 'has-error' : ''}`}>
                                        <label>{translations?.company || 'Company'}</label>
                                        <input
                                            type="text"
                                            value={item.company || ''}
                                            onChange={(e) => onItemChange(index, 'company', e.target.value)}
                                            placeholder={translations?.company || 'Company'}
                                            disabled={disabled}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        />
                                        {errors[`company_${index}`] && (
                                            <div className="error-text">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                                {errors[`company_${index}`]}
                                            </div>
                                        )}
                                    </div>

                                    {/* Unit */}
                                    <div className={`mobile-field ${errors[`unit_${index}`] ? 'has-error' : ''}`}>
                                        <label>{translations?.unit || 'Unit'}</label>
                                        <select
                                            value={item.unit}
                                            onChange={(e) => onItemChange(index, 'unit', e.target.value)}
                                            disabled={disabled}
                                            dir={isRTL ? 'rtl' : 'ltr'}
                                        >
                                            <option value="">{translations?.selectUnit || 'Select'}</option>
                                            {units.map((unit) => (
                                                <option key={unit.value} value={unit.value}>{unit.label}</option>
                                            ))}
                                        </select>
                                        {errors[`unit_${index}`] && (
                                            <div className="error-text">{errors[`unit_${index}`]}</div>
                                        )}
                                    </div>

                                    {/* Quantity */}
                                    <div className={`mobile-field ${errors[`quantity_${index}`] ? 'has-error' : ''}`}>
                                        <label>{translations?.quantity || 'Quantity'} *</label>
                                        <input
                                            type="number"
                                            value={item.quantity}
                                            onChange={(e) => onItemChange(index, 'quantity', e.target.value)}
                                            step="0.001"
                                            min="0.001"
                                            placeholder="0.000"
                                            disabled={disabled}
                                            dir="ltr"
                                        />
                                        {errors[`quantity_${index}`] && (
                                            <div className="error-text">{errors[`quantity_${index}`]}</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // Desktop view
    return (
        <div className="items-table-container">
            <div className="table-header-actions">
                <h4>
                    <FontAwesomeIcon icon={faBoxes} className="header-icon-only" />
                    {translations?.items || 'Items'}
                </h4>
                <button type="button" className="btn-add-item-table" onClick={onAdd} disabled={disabled}>
                    <FontAwesomeIcon icon={faPlus} />
                    <span>{translations?.addItem || 'Add Item'}</span>
                </button>
            </div>
            <div className="table-wrapper" ref={tableRef}>
                <table className="items-table">
                    <thead>
                        <tr>
                            <th className="sticky-col product-col">{translations?.productName || 'Product Name'}</th>
                            <th className="sticky-col batch-col">{translations?.batchNumber || 'Batch No'}</th>
                            <th className="sticky-col category-col">{translations?.category || 'Category'}</th>
                            <th className="sticky-col stock-category-col">{translations?.stockCategory || 'Stock Category'}</th>
                            <th className="sticky-col company-col">{translations?.company || 'Company'}</th>
                            <th className="sticky-col unit-col">{translations?.unit || 'Unit'}</th>
                            <th className="sticky-col qty-col">{translations?.quantity || 'Quantity'} <span className="required">*</span></th>
                            <th className="sticky-col action-col"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item, index) => {
                            const filteredProductsForIndex = getFilteredProductsForIndex(index);
                            const showDropdown = showProductDropdown[index] || false;
                            const searchTerm = productSearchTerms[index] !== undefined ? productSearchTerms[index] : item.product_name;
                            
                            return (
                                <tr key={index} className={errors[`product_name_${index}`] ? 'error-row' : ''}>
                                    {/* Product Name */}
                                    <td>
                                        <div className="table-field product-search-field" ref={el => productSearchRefs.current[index] = el}>
                                            <div className="product-search-container">
                                                <input
                                                    type="text"
                                                    value={searchTerm}
                                                    onChange={(e) => handleProductSearch(index, e.target.value)}
                                                    onFocus={() => {
                                                        if (searchTerm && searchTerm.trim()) {
                                                            handleProductSearch(index, searchTerm);
                                                        } else {
                                                            setShowProductDropdown(prev => ({ ...prev, [index]: true }));
                                                        }
                                                    }}
                                                    placeholder={translations?.searchProduct || 'Search product...'}
                                                    disabled={disabled}
                                                    dir={isRTL ? 'rtl' : 'ltr'}
                                                    className={errors[`product_name_${index}`] ? 'error' : ''}
                                                    autoComplete="off"
                                                />
                                                {loadingProducts && <div className="search-loading-small"><FontAwesomeIcon icon={faSpinner} spin /></div>}

                                                {showDropdown && filteredProductsForIndex.length > 0 && (
                                                    <div className="product-dropdown-table">
                                                        {filteredProductsForIndex.map(product => (
                                                            <div key={product.id} className="product-option" onClick={() => selectProduct(index, product)}>
                                                                <div className="product-name">{product.product_name}</div>
                                                                <div className="product-details">
                                                                    {product.batch_no && <span><FontAwesomeIcon icon={faTag} /> Batch: {product.batch_no}</span>}
                                                                    {product.unit && <span>Unit: {product.unit}</span>}
                                                                </div>
                                                                <div className="product-stock">Stock: {product.quantity}</div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                                {showDropdown && filteredProductsForIndex.length === 0 && searchTerm && (
                                                    <div className="product-dropdown-table empty">
                                                        <FontAwesomeIcon icon={faExclamationTriangle} />
                                                        {translations?.noProductsFound || 'No products found'}
                                                    </div>
                                                )}
                                            </div>
                                            {errors[`product_name_${index}`] && (
                                                <div className="table-error">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    {errors[`product_name_${index}`]}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    {/* Batch Number */}
                                    <td>
                                        <div className="table-field">
                                            <input
                                                type="text"
                                                value={item.batch_no || ''}
                                                onChange={(e) => onItemChange(index, 'batch_no', e.target.value)}
                                                placeholder={translations?.batchNumber || 'Batch no'}
                                                disabled={disabled}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                                className={errors[`batch_no_${index}`] ? 'error' : ''}
                                                style={{ backgroundColor: item.batch_no ? '#e8f5e9' : '', borderLeft: item.batch_no ? '3px solid #4caf50' : '' }}
                                            />
                                            {errors[`batch_no_${index}`] && (
                                                <div className="table-error">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    {errors[`batch_no_${index}`]}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    {/* Category */}
                                    <td>
                                        <div className="table-field">
                                            <input
                                                type="text"
                                                value={item.category || ''}
                                                onChange={(e) => onItemChange(index, 'category', e.target.value)}
                                                placeholder={translations?.category || 'Category'}
                                                disabled={disabled}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                                className={errors[`category_${index}`] ? 'error' : ''}
                                            />
                                            {errors[`category_${index}`] && (
                                                <div className="table-error">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    {errors[`category_${index}`]}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    {/* Stock Category */}
                                    <td>
                                        <div className="table-field">
                                            <input
                                                type="text"
                                                value={item.stock_category || ''}
                                                onChange={(e) => onItemChange(index, 'stock_category', e.target.value)}
                                                placeholder={translations?.stockCategory || 'Stock Category'}
                                                disabled={disabled}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                                className={errors[`stock_category_${index}`] ? 'error' : ''}
                                            />
                                            {errors[`stock_category_${index}`] && (
                                                <div className="table-error">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    {errors[`stock_category_${index}`]}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    {/* Company */}
                                    <td>
                                        <div className="table-field">
                                            <input
                                                type="text"
                                                value={item.company || ''}
                                                onChange={(e) => onItemChange(index, 'company', e.target.value)}
                                                placeholder={translations?.company || 'Company'}
                                                disabled={disabled}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                                className={errors[`company_${index}`] ? 'error' : ''}
                                            />
                                            {errors[`company_${index}`] && (
                                                <div className="table-error">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    {errors[`company_${index}`]}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    {/* Unit */}
                                    <td>
                                        <div className="table-field">
                                            <select
                                                value={item.unit}
                                                onChange={(e) => onItemChange(index, 'unit', e.target.value)}
                                                disabled={disabled}
                                                dir={isRTL ? 'rtl' : 'ltr'}
                                                className={errors[`unit_${index}`] ? 'error' : ''}
                                            >
                                                <option value="">{translations?.selectUnit || 'Select'}</option>
                                                {units.map((unit) => (
                                                    <option key={unit.value} value={unit.value}>{unit.label}</option>
                                                ))}
                                            </select>
                                            {errors[`unit_${index}`] && (
                                                <div className="table-error">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    {errors[`unit_${index}`]}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    {/* Quantity */}
                                    <td>
                                        <div className="table-field">
                                            <input
                                                type="number"
                                                value={item.quantity}
                                                onChange={(e) => onItemChange(index, 'quantity', e.target.value)}
                                                step="0.001"
                                                min="0.001"
                                                placeholder="0.000"
                                                disabled={disabled}
                                                dir="ltr"
                                                className={errors[`quantity_${index}`] ? 'error' : ''}
                                            />
                                            {errors[`quantity_${index}`] && (
                                                <div className="table-error">
                                                    <FontAwesomeIcon icon={faExclamationTriangle} />
                                                    {errors[`quantity_${index}`]}
                                                </div>
                                            )}
                                        </div>
                                    </td>
                                    {/* Remove Button */}
                                    <td>
                                        <button type="button" className="table-remove-btn" onClick={() => onRemove(index)} disabled={disabled || items.length === 1} title={translations?.remove || 'Remove'}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const AddStock = ({ purchaseId = null, onSuccess = null }) => {
    const { user } = useAuth();

    const [shopId, setShopId] = useState(() => {
        if (user?.shop_id) return user.shop_id;
        return localStorage.getItem('current_shop_id') || '';
    });

    const [formData, setFormData] = useState({
        date: new Date().toISOString().split('T')[0],
        bill_file: null,
        grand_total: '0.00',
        items: [{
            product_name: '',
            batch_no: '',
            category: '',
            stock_category: '',
            company: '',
            unit: '',
            quantity: ''
        }],
        themeColor: localStorage.getItem('ourzest_theme') || '#8b5cf6'
    });

    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [currentLanguage, setCurrentLanguage] = useState(() => localStorage.getItem('ourzest_language') || 'en');
    const [translations, setTranslations] = useState({});
    const [isTranslating, setIsTranslating] = useState(false);

    const [toast, setToast] = useState({ show: false, type: '', message: '', direction: 'ltr', progress: 100, timeLeft: 3 });

    const toastTimerRef = useRef(null);
    const fileInputRef = useRef(null);

    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) clearInterval(toastTimerRef.current);
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const direction = isRTL ? 'rtl' : 'ltr';
        setToast({ show: true, type, message, direction, progress: 100, timeLeft: 3 });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;
            setToast(prev => ({ ...prev, progress, timeLeft: parseFloat(timeLeft.toFixed(1)) }));
            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);
        toastTimerRef.current = timer;
    }, [currentLanguage]);

    useEffect(() => { return () => { if (toastTimerRef.current) clearInterval(toastTimerRef.current); }; }, []);

    const loadTranslations = useCallback(async (lang) => {
        if (!lang) return;
        setIsTranslating(true);
        try {
            const trans = await purchasesTranslator.getTranslations(lang);
            setTranslations(trans);
            const isRTL = lang === 'ur' || lang === 'ar';
            document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
            document.body.dir = isRTL ? 'rtl' : 'ltr';
        } catch (error) { console.error('Error loading translations:', error); }
        finally { setIsTranslating(false); }
    }, []);

    useEffect(() => {
        loadTranslations(currentLanguage);
        const handleLanguageChange = () => {
            const newLang = localStorage.getItem('ourzest_language') || 'en';
            if (newLang !== currentLanguage) { setCurrentLanguage(newLang); loadTranslations(newLang); }
        };
        window.addEventListener('storage', handleLanguageChange);
        const intervalId = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) { setCurrentLanguage(storedLang); loadTranslations(storedLang); }
        }, 500);
        const translationListener = (lang) => { if (lang !== currentLanguage) { setCurrentLanguage(lang); loadTranslations(lang); } };
        purchasesTranslator.addListener(translationListener);
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            clearInterval(intervalId);
            purchasesTranslator.removeListener(translationListener);
        };
    }, [currentLanguage, loadTranslations]);

    useEffect(() => {
        const applyTheme = (color) => {
            try {
                if (!/^#[0-9A-F]{6}$/i.test(color)) color = '#8b5cf6';
                const hex = color.replace('#', '');
                const r = parseInt(hex.substring(0, 2), 16);
                const g = parseInt(hex.substring(2, 4), 16);
                const b = parseInt(hex.substring(4, 6), 16);
                const adjust = (amount) => {
                    let newR = Math.max(0, Math.min(255, r + amount));
                    let newG = Math.max(0, Math.min(255, g + amount));
                    let newB = Math.max(0, Math.min(255, b + amount));
                    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
                };
                const palette = {
                    'purchase-primary-color': color,
                    'purchase-primary-dark': adjust(-40),
                    'purchase-primary-light': adjust(40),
                    'purchase-primary-ultralight': adjust(20),
                    'purchase-primary-gradient': `linear-gradient(135deg, ${color}, ${adjust(-20)})`,
                    'purchase-primary-shadow': `${color}20`
                };
                Object.entries(palette).forEach(([key, value]) => { document.documentElement.style.setProperty(`--${key}`, value); });
            } catch (err) { console.error("Error applying theme:", err); }
        };
        applyTheme(formData.themeColor);
        const handleThemeChange = () => {
            const theme = localStorage.getItem('ourzest_theme') || '#8b5cf6';
            if (theme !== formData.themeColor) { setFormData(prev => ({ ...prev, themeColor: theme })); applyTheme(theme); }
        };
        handleThemeChange();
        window.addEventListener('storage', handleThemeChange);
        const interval = setInterval(handleThemeChange, 1000);
        return () => { window.removeEventListener('storage', handleThemeChange); clearInterval(interval); };
    }, [formData.themeColor]);

    useEffect(() => { if (purchaseId) { setIsEditMode(true); loadPurchaseData(purchaseId); } }, [purchaseId]);

    const loadPurchaseData = async (id) => {
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/addstock.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_record', id: id, username: user?.username, shop_id: shopId })
            });
            const data = await response.json();
            if (data.status === 'success' && data.data) {
                const purchase = data.data;
                setFormData({
                    date: purchase.date || new Date().toISOString().split('T')[0],
                    bill_file: null,
                    grand_total: purchase.grand_total || '0.00',
                    items: purchase.items && purchase.items.length > 0 ? purchase.items : [{
                        product_name: '',
                        batch_no: '',
                        category: '',
                        stock_category: '',
                        company: '',
                        unit: '',
                        quantity: ''
                    }],
                    themeColor: formData.themeColor
                });
            } else { showToast('error', data.message || 'Failed to load stock data'); }
        } catch (error) { showToast('error', 'Network error occurred'); }
        finally { setLoading(false); }
    };

    const handleItemChange = (index, field, value) => {
        console.log(`handleItemChange: item ${index}, field ${field}, value:`, value);
        setFormData(prev => {
            const updatedItems = [...prev.items];
            updatedItems[index] = { ...updatedItems[index], [field]: value };
            return { ...prev, items: updatedItems };
        });
        const errorKey = `${field}_${index}`;
        if (errors[errorKey]) {
            setErrors(prev => { const newErrors = { ...prev }; delete newErrors[errorKey]; return newErrors; });
        }
    };

    const addItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, {
                product_name: '',
                batch_no: '',
                category: '',
                stock_category: '',
                company: '',
                unit: '',
                quantity: ''
            }]
        }));
    };

    const removeItem = (index) => {
        if (formData.items.length > 1) {
            setFormData(prev => {
                const updatedItems = [...prev.items];
                updatedItems.splice(index, 1);
                return { ...prev, items: updatedItems };
            });
            const fields = ['product_name', 'batch_no', 'category', 'stock_category', 'company', 'unit', 'quantity'];
            setErrors(prev => {
                const newErrors = { ...prev };
                fields.forEach(field => { delete newErrors[`${field}_${index}`]; });
                return newErrors;
            });
        }
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) { showToast('error', 'File size must be less than 5MB'); return; }
            const reader = new FileReader();
            reader.onload = (event) => {
                setFormData(prev => ({ ...prev, bill_file: { name: file.name, type: file.type, data: event.target.result.split(',')[1] } }));
            };
            reader.readAsDataURL(file);
        }
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.date) newErrors.date = translations?.dateRequired || 'Date is required';
        if (!formData.items || formData.items.length === 0) {
            newErrors.items = translations?.itemsRequired || 'At least one item is required';
        } else {
            formData.items.forEach((item, index) => {
                if (!item.product_name?.trim()) newErrors[`product_name_${index}`] = translations?.productRequired || 'Product name is required';
                if (!item.batch_no?.trim()) newErrors[`batch_no_${index}`] = translations?.batchRequired || 'Batch number is required';
                if (!item.unit) newErrors[`unit_${index}`] = translations?.unitRequired || 'Unit is required';
                if (!item.quantity || parseFloat(item.quantity) <= 0) newErrors[`quantity_${index}`] = translations?.quantityRequired || 'Quantity is required';
            });
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.log('Submitting form data:', formData.items);
        if (!validateForm()) {
            const firstError = document.querySelector('.has-error, .error-message-modern');
            if (firstError) firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        setSaving(true);
        try {
            const currentUsername = user?.username || 'Unknown';
            const currentShopId = shopId || localStorage.getItem('current_shop_id');
            if (!currentUsername || currentUsername === 'Unknown') throw new Error("User not authenticated");
            if (!currentShopId) throw new Error("Shop ID is required. Please select a shop first.");

            const action = isEditMode ? 'edit' : 'add';
            const endpoint = 'https://ourzestapp.online/oz_backend/addstock.php';

            // Process items - include all fields
            const processedItems = formData.items.map(item => ({
                product_name: item.product_name || '',
                batch_no: item.batch_no || '',
                category: item.category || '',
                stock_category: item.stock_category || '',
                company: item.company || '',
                unit: item.unit || '',
                quantity: parseFloat(item.quantity || 0).toFixed(3)
            }));

            console.log('Processed items:', processedItems);

            const payload = {
                action: action,
                username: currentUsername,
                shop_id: parseInt(currentShopId),
                date: formData.date,
                grand_total: '0.00',
                items: processedItems
            };
            if (formData.bill_file) payload.bill_file = JSON.stringify(formData.bill_file);
            if (isEditMode && purchaseId) payload.id = purchaseId;

            console.log('Submitting payload:', JSON.stringify(payload, null, 2));

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const responseText = await response.text();
            console.log('Raw response:', responseText);

            let data;
            try { data = JSON.parse(responseText); console.log('Parsed response:', data); }
            catch (parseError) { console.error('Failed to parse response as JSON:', parseError); throw new Error(`Server returned invalid JSON: ${responseText.substring(0, 200)}`); }

            if (data.status === 'success') {
                const successMessage = isEditMode ? (translations?.purchaseUpdated || 'Stock updated successfully!') : (translations?.purchaseSaved || 'Stock saved successfully!');
                showToast('success', successMessage);
                if (!isEditMode) {
                    setFormData({
                        date: new Date().toISOString().split('T')[0],
                        bill_file: null,
                        grand_total: '0.00',
                        items: [{
                            product_name: '',
                            batch_no: '',
                            category: '',
                            stock_category: '',
                            company: '',
                            unit: '',
                            quantity: ''
                        }],
                        themeColor: formData.themeColor
                    });
                    setErrors({});
                }
                if (fileInputRef.current) fileInputRef.current.value = '';
                if (onSuccess) onSuccess(data.purchaseid || purchaseId);
            } else {
                const errorMessage = data.message || (isEditMode ? translations?.failedUpdate || 'Failed to update stock' : translations?.failedSave || 'Failed to save stock');
                showToast('error', errorMessage);
                console.error('Server error:', data);
            }
        } catch (error) {
            console.error('Submission error:', error);
            showToast('error', error.message || translations?.networkError || 'Network error occurred');
        } finally { setSaving(false); }
    };

    const handleClear = () => {
        setFormData({
            date: new Date().toISOString().split('T')[0],
            bill_file: null,
            grand_total: '0.00',
            items: [{
                product_name: '',
                batch_no: '',
                category: '',
                stock_category: '',
                company: '',
                unit: '',
                quantity: ''
            }],
            themeColor: formData.themeColor
        });
        setErrors({});
        setIsEditMode(false);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    return (
        <div className={`purchases-form-independent ${isRTL ? 'rtl' : ''} ${isTranslating ? 'translating' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} translations={translations} />

            <div className="form-header-modern">
                <div className="header-content">
                    <div className="header-icon"><FontAwesomeIcon icon={isEditMode ? faShoppingCart : faPlus} /></div>
                    <div className="header-text">
                        <h1>{isEditMode ? (translations?.editPurchase || 'Edit Stock') : (translations?.addPurchase || 'Add Stock')}</h1>
                        <p>{translations?.purchaseDetails || 'Stock Details'}</p>
                    </div>
                </div>
                {isTranslating && (<div className="translation-indicator"><FontAwesomeIcon icon={faSpinner} spin /><span>Translating...</span></div>)}
            </div>

            <form onSubmit={handleSubmit} className="modern-form">
                <div className="form-section">
                    <h3 className="section-title"><FontAwesomeIcon icon={faReceipt} />{translations?.purchaseDetails || 'Stock Information'}</h3>
                    <div className="form-grid-modern">
                        <div className={`form-group-modern ${errors.date ? 'has-error' : ''}`}>
                            <div className="input-container">
                                <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                <input type="date" id="date" name="date" value={formData.date} onChange={handleChange} disabled={saving} dir="ltr" />
                                <label htmlFor="date" className="input-label">{translations?.date || 'Date'}<span className="required">*</span></label>
                            </div>
                            {errors.date && (<div className="error-message-modern"><FontAwesomeIcon icon={faExclamationTriangle} />{errors.date}</div>)}
                        </div>
                        <div className="form-group-modern phone-file-row">
                            <div className="input-container file-upload-container file-input-compact">
                                <FontAwesomeIcon icon={faUpload} className="input-icon" />
                                <input type="file" id="bill_file" ref={fileInputRef} onChange={handleFileChange} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" disabled={saving} className="file-input" />
                                <label htmlFor="bill_file" className="file-upload-label">
                                    <span className="file-text">{formData.bill_file ? formData.bill_file.name : translations?.chooseFile || 'Choose file'}</span>
                                    <span className="file-browse"><FontAwesomeIcon icon={faUpload} />{translations?.browse || 'Browse'}</span>
                                </label>
                                <label className="input-label">{translations?.billFile || 'Bill File'}<span className="optional">{translations?.optional ? ` (${translations.optional})` : ' (Optional)'}</span></label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-section">
                    <ItemsTable items={formData.items} onItemChange={handleItemChange} onRemove={removeItem} onAdd={addItem} errors={errors} disabled={saving} translations={translations} isRTL={isRTL} user={user} shopId={shopId} showToast={showToast} />
                </div>

                <div className="form-actions-modern">
                    <button type="button" className="btn-modern btn-secondary-modern" onClick={handleClear} disabled={saving}>
                        <FontAwesomeIcon icon={faTimes} /><span>{translations?.clear || 'Clear'}</span>
                    </button>
                    <button type="submit" className="btn-modern btn-primary-modern" disabled={saving || isTranslating}>
                        {saving ? (<><div className="btn-spinner"></div><span>{translations?.saving || 'Saving...'}</span></>) : (<><span>{isEditMode ? (translations?.updatePurchase || 'Update') : (translations?.savePurchase || 'Save')}</span><FontAwesomeIcon icon={faSave} /></>)}
                    </button>
                </div>
            </form>

            {loading && (<div className="loading-overlay"><div className="loading-content"><div className="loading-spinner"></div><p>{translations?.saving || 'Loading...'}</p><p className="loading-subtext">Please wait while we load stock data</p></div></div>)}
        </div>
    );
};

export default AddStock;